import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {filter, map} from 'lodash';
import Button from 'src/components/button';
import Checkbox from 'shared/components/checkbox';
import FileField from '../FileField/FileField';
import CollegePhoto from '../CollegePhoto';
import {
  fetchPhotoListings,
  resetPhotoListingCounter,
  updateUseRankedState,
} from 'src/redux/adminUniversity/actions/photoListing';
import {Store} from 'src/types/store';
import {useBoolean} from 'src/hooks/useBoolean';
import {useField} from 'formik';
import {DeleteCollegeImage} from '../form/form';

interface Props {
  universityId: string;
}

const PhotoListings = ({universityId}: Props) => {
  const dispatch = useDispatch();
  const photoListings = useSelector((state: Store) => state.photoListings);

  const [filesDropped, setFilesDropped] = useState<Set<$TSFixMe>>(new Set());
  const [filesDroppedNames, setFilesDroppedNames] = useState<Set<string>>(new Set());
  const [useDeletedState, setUseDeletedState] = useState<boolean>(false);
  const showAdded = useBoolean(true);
  const [deleteCollegeImages, , deleteCollegeImagesHelpers] =
    useField<Set<DeleteCollegeImage>>('deleteCollegeImages');
  const [useRanked, , useRankedHelpers] = useField<boolean>('useRanked');

  const updateFilesState = (newFilesDropped: Set<$TSFixMe>, newFilesDroppedNames: Set<string>) => {
    setFilesDropped(newFilesDropped);
    setFilesDroppedNames(newFilesDroppedNames);
  };

  const resetFilesState = () => {
    dispatch(resetPhotoListingCounter());
    setFilesDropped(new Set());
    setFilesDroppedNames(new Set());
  };

  useEffect(() => {
    dispatch(fetchPhotoListings(universityId));
  }, [dispatch, universityId]);

  const deletedValues = () => {
    if (deleteCollegeImages.value.size === 0 && !useDeletedState) {
      return new Set(photoListings.deleted);
    }
    return deleteCollegeImages.value;
  };

  const deletePhoto = (url) => {
    const currentDeleted = deletedValues();
    return (e) => {
      e.preventDefault();
      const newDeleteState = new Set(currentDeleted);
      if (currentDeleted.has(url)) {
        newDeleteState.delete(url);
      } else {
        newDeleteState.add(url);
      }
      deleteCollegeImagesHelpers.setValue(newDeleteState);
      setUseDeletedState(true);
    };
  };

  const processPhotoShow = (photoUrls) => {
    const currentDeleted = deletedValues();
    const showCriteria = showAdded.value ? 'Show Added' : 'Show Deleted';
    if (photoUrls.length === 0) {
      return <div>{`No ${showCriteria.slice(5)} Photos`}</div>;
    }
    const filteredPhotos = filter(photoUrls, (url) => {
      if (
        (showAdded.value && !currentDeleted.has(url)) || // Show Added
        (!showAdded.value && currentDeleted.has(url)) // Show Deleted
      ) {
        return url;
      }
    });
    const mappedPhotos = map(filteredPhotos, (url, i) => (
      <CollegePhoto
        key={`CollegePhoto${showCriteria}:${i}`}
        url={url}
        isDeleted={currentDeleted.has(url)}
        deletePhoto={deletePhoto}
      />
    ));
    return mappedPhotos.length === 0 ? (
      <div>{`No ${showCriteria.slice(5)} Photos`}</div>
    ) : (
      mappedPhotos
    );
  };

  const renderFiles = () => {
    const rankedData = useRanked.value ? photoListings.ranked : [];
    return processPhotoShow(rankedData.concat(photoListings.added));
  };

  useEffect(() => {
    if (photoListings.fileProgressCount === 0 && photoListings.previousCount !== -1) {
      resetFilesState();
    }
  }, [photoListings.fileProgressCount, photoListings.previousCount]);

  if (!photoListings) {
    return null;
  }

  const addedText = 'Added Photos';
  const deletedText = 'Deleted Photos';

  return (
    <div>
      <div className="shared-label">
        <span className="shared-label-text">College Listing Photos</span>
      </div>
      <FileField
        filesDropped={filesDropped}
        filesDroppedNames={filesDroppedNames}
        updateFilesState={updateFilesState}
        counter={photoListings.fileProgressCount}
      />
      <Checkbox
        checked={useRanked.value}
        label="Use Instagram Photos"
        onChange={() => {
          dispatch(updateUseRankedState(!useRanked.value));
          useRankedHelpers.setValue(!useRanked.value);
        }}
      />
      <div className="shared-label admin-portal-college-photo-header">
        <span className="shared-label-text">{showAdded.value ? addedText : deletedText}</span>
        <Button onClick={showAdded.toggle}>
          {showAdded.value ? 'Show Deleted' : 'Show Added'}
        </Button>
      </div>
      <section className="admin-portal-college-photos-container">{renderFiles()}</section>
    </div>
  );
};

export default PhotoListings;
